import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, DownloadCloud as DownloadCloudIcon } from 'lucide-react';
import {
  getConfigSugeridaEmpresa,
  getEmpresa,
  updateEmpresa,
  verificarEmpresaOracle,
  type UpdateEmpresaPayload,
} from '@lib/services/empresas.service';
import { useCanMutate } from '@lib/hooks/useCanMutate';
import { PlanesLista, type PlanForm } from '@components/empresas/PlanesLista';
import { DdjjOrigenSelector, type DdjjPropioForm } from '@components/empresas/DdjjOrigenSelector';
import { ddjjConfigPayload } from '@lib/empresas/ddjj-config-payload';
import { UsuariosDeEmpresa } from '@components/empresas/UsuariosDeEmpresa';
import { fusionar, resumenVacio, type ResumenOracle } from '@lib/empresas/fusionar-config';
import { planesAPayload } from '@lib/empresas/planes-payload';

/**
 * Detalle de una organización, en su propia URL (`/empresas/:id`).
 *
 * Antes todo esto vivía en un modal: con identidad, alta, planes, DDJJ y
 * usuarios encima, no entraba en pantalla y no se podía linkear ni volver
 * atrás. Como vista tiene lugar para subsecciones, y la pestaña activa viaja
 * en la query (`?tab=planes`) para poder compartir el link exacto.
 */
type Tab = 'datos' | 'planes' | 'ddjj' | 'usuarios';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'datos', label: 'Datos' },
  { id: 'planes', label: 'Alta y planes' },
  { id: 'ddjj', label: 'Declaración jurada' },
  { id: 'usuarios', label: 'Usuarios' },
];

interface FormState {
  cuit: string;
  razonSocial: string;
  nombreComercial: string;
  contactoNombre: string;
  contactoEmail: string;
  contactoTelefono: string;
  oracleEmplId: string;
  altaHabilitada: boolean;
  /** Sin asistente del titular: los legajos pasan directo al alta al validar el padrón. */
  sinWizard: boolean;
  obraSocialCodigo: string;
  promotorId: string;
  firmaSolicitud: string;
  agenciaEnvio: string;
  planes: PlanForm[];
  ddjjExcluidas: string[];
  /** Cuestionario propio (Binary/Excel). `null` = catálogo del micrositio. */
  ddjjPropio: DdjjPropioForm | null;
}

export function EmpresaDetallePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const puedeMutar = useCanMutate();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'datos';
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [oracleNotas, setOracleNotas] = useState<string[]>([]);
  const [oracleResumen, setOracleResumen] = useState<ResumenOracle>(resumenVacio());

  const empresaQ = useQuery({
    queryKey: ['empresa', id],
    queryFn: () => getEmpresa(id),
    enabled: !!id,
  });

  // El form se hidrata una sola vez con lo que vino del server; a partir de
  // ahí manda el estado local (si no, cada refetch pisaría lo que se edita).
  useEffect(() => {
    const e = empresaQ.data;
    if (!e || form) return;
    const ac = e.altaConfig;
    let planes: PlanForm[] = (ac?.planes ?? []).map((p) => ({
      codigo: p.codigo ?? '',
      nombre: p.nombre ?? '',
      sucursalId: p.sucursalId ?? '',
      categoriaAfiliadoCodigo: p.categoriaAfiliadoCodigo ?? '',
      aaCodigo: p.aaCodigo ?? '',
      aa2Codigo: p.aa2Codigo ?? '',
      esBase: p.esBase ?? false,
      disponibleVenta: p.disponibleVenta !== false,
    }));
    // Back-compat: organización vieja con planBaseCodigo suelto y sin planes[].
    if (planes.length === 0 && e.planBaseCodigo) {
      planes = [
        {
          codigo: e.planBaseCodigo,
          nombre: '',
          sucursalId: ac?.sucursalId ?? '',
          categoriaAfiliadoCodigo: ac?.categoriaAfiliadoCodigo ?? '',
          aaCodigo: ac?.aaCodigo ?? '',
          aa2Codigo: ac?.aa2Codigo ?? '',
          esBase: true,
        },
        ...(e.planSuperadorCodigo
          ? [
              {
                codigo: e.planSuperadorCodigo,
                nombre: '',
                sucursalId: '',
                categoriaAfiliadoCodigo: '',
                aaCodigo: '',
                aa2Codigo: '',
                esBase: false,
              },
            ]
          : []),
      ];
    }
    setForm({
      cuit: e.cuit,
      razonSocial: e.razonSocial,
      nombreComercial: e.nombreComercial ?? '',
      contactoNombre: e.contactoNombre ?? '',
      contactoEmail: e.contactoEmail ?? '',
      contactoTelefono: e.contactoTelefono ?? '',
      oracleEmplId: e.oracleEmplId ? String(e.oracleEmplId) : '',
      altaHabilitada: ac?.habilitada ?? false,
      sinWizard: e.sinWizard === true,
      obraSocialCodigo: ac?.obraSocialCodigo ?? '',
      promotorId: ac?.promotorId ?? '',
      firmaSolicitud: ac?.firmaSolicitud ?? '',
      agenciaEnvio: ac?.agenciaEnvio ?? '',
      planes,
      ddjjExcluidas: e.ddjjConfig?.preguntasExcluidas ?? [],
      ddjjPropio:
        e.ddjjConfig?.preguntas && e.ddjjConfig.preguntas.length > 0
          ? {
              preguntas: e.ddjjConfig.preguntas,
              origen: e.ddjjConfig.origen === 'excel' ? 'excel' : 'binary',
              nuevo: false,
            }
          : null,
    });
  }, [empresaQ.data, form]);

  const emplIdNum = Number.parseInt(form?.oracleEmplId ?? '', 10);
  const emplIdValido = Number.isFinite(emplIdNum) && emplIdNum > 0;

  const check = useQuery({
    queryKey: ['oracle-empresa-check', emplIdNum],
    queryFn: () => verificarEmpresaOracle(emplIdNum),
    enabled: emplIdValido,
    retry: false,
    staleTime: 60_000,
  });

  const guardar = useMutation({
    mutationFn: (payload: UpdateEmpresaPayload) => updateEmpresa(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresa', id] });
      qc.invalidateQueries({ queryKey: ['empresas'] });
      setError(null);
      setOkMsg('Cambios guardados.');
      setTimeout(() => setOkMsg(null), 3000);
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(err.response?.data?.error?.message ?? 'No se pudo guardar.');
    },
  });

  const traer = useMutation({
    mutationFn: () => getConfigSugeridaEmpresa(emplIdNum),
    onSuccess: (cfg) => {
      if (!form) return;
      // La fusión se calcula ACÁ y no dentro del updater de setForm: `fusionar`
      // acumula mensajes, y un updater con efectos se ejecuta dos veces bajo
      // StrictMode (cada línea del resumen salía duplicada).
      const resumen = resumenVacio();
      const razonSocial = fusionar('Razón social', form.razonSocial, cfg.identidad.nombre, resumen);
      const cuit = fusionar('CUIT', form.cuit, cfg.identidad.cuit, resumen);
      // El nombre comercial se completa del mismo NOMBRE de Oracle; si ya hay uno
      // cargado no se repite la diferencia (la razón social ya la reportó).
      const nombreComercial = fusionar(
        'Nombre comercial',
        form.nombreComercial,
        cfg.identidad.nombre,
        resumen,
        false,
      );
      setForm((p) =>
        !p
          ? p
          : {
              ...p,
              razonSocial,
              cuit,
              nombreComercial,
              obraSocialCodigo: cfg.obraSocialCodigo ?? p.obraSocialCodigo,
              promotorId: cfg.promotorId ?? p.promotorId,
              firmaSolicitud: cfg.firmaSolicitud ?? p.firmaSolicitud,
              agenciaEnvio: cfg.agenciaEnvio || p.agenciaEnvio || '1',
              planes: cfg.planes.length
                ? cfg.planes.map((pl, i) => ({
                    codigo: pl.codigo,
                    nombre: pl.nombre,
                    sucursalId: pl.sucursalId,
                    categoriaAfiliadoCodigo: pl.categoriaAfiliadoCodigo,
                    aaCodigo: pl.aaCodigo,
                    aa2Codigo: pl.aa2Codigo,
                    esBase: i === 0,
                  }))
                : p.planes,
            },
      );
      if (cfg.planes.length) resumen.aplicados.push(`${cfg.planes.length} plan(es) de Oracle`);
      resumen.procedencia.push(...(cfg.procedencia ?? []));
      setOracleResumen(resumen);
      setOracleNotas(cfg.notas ?? []);
      setError(null);
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(err.response?.data?.error?.message ?? 'No se pudo traer la config de Oracle.');
    },
  });

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => (p ? { ...p, [k]: v } : p));

  const setPlan = (i: number, k: keyof PlanForm, v: string) =>
    setForm((p) =>
      p ? { ...p, planes: p.planes.map((pl, idx) => (idx === i ? { ...pl, [k]: v } : pl)) } : p,
    );
  const addPlan = () =>
    setForm((p) =>
      p
        ? {
            ...p,
            planes: [
              ...p.planes,
              {
                codigo: '',
                nombre: '',
                sucursalId: '',
                categoriaAfiliadoCodigo: '',
                aaCodigo: '',
                aa2Codigo: '',
                esBase: p.planes.length === 0,
              },
            ],
          }
        : p,
    );
  const removePlan = (i: number) =>
    setForm((p) => {
      if (!p) return p;
      const planes = p.planes.filter((_, idx) => idx !== i);
      if (planes.length > 0 && !planes.some((pl) => pl.esBase)) planes[0].esBase = true;
      return { ...p, planes };
    });
  const setBase = (i: number) =>
    setForm((p) =>
      p ? { ...p, planes: p.planes.map((pl, idx) => ({ ...pl, esBase: idx === i })) } : p,
    );

  // Como mucho hay UN plan inicial: marcar uno desmarca al resto, y `-1`
  // desmarca todos (la organización vuelve a dar altas con el plan base).
  // Un plan sin el flag cuenta como disponible, así que solo se guarda cuando
  // se apaga o se vuelve a prender explícitamente.
  const setVenta = (i: number, valor: boolean) =>
    setForm((p) =>
      p
        ? {
            ...p,
            planes: p.planes.map((pl, idx) => (idx === i ? { ...pl, disponibleVenta: valor } : pl)),
          }
        : p,
    );

  const submit = () => {
    if (!form) return;
    const planes = planesAPayload(form.planes);
    const base = planes.find((p) => p.esBase) ?? planes[0];
    // Superador = otro CÓDIGO: el mismo plan en otra sucursal es la misma cobertura.
    const superador = planes.find((p) => !p.esBase && p.codigo !== base?.codigo);
    guardar.mutate({
      cuit: form.cuit,
      razonSocial: form.razonSocial,
      nombreComercial: form.nombreComercial,
      contactoNombre: form.contactoNombre,
      contactoEmail: form.contactoEmail,
      contactoTelefono: form.contactoTelefono || undefined,
      oracleEmplId: emplIdValido ? emplIdNum : undefined,
      planBaseCodigo: base?.codigo,
      planSuperadorCodigo: superador?.codigo,
      altaConfig: {
        habilitada: form.altaHabilitada,
        obraSocialCodigo: form.obraSocialCodigo || undefined,
        promotorId: form.promotorId || undefined,
        firmaSolicitud: form.firmaSolicitud || undefined,
        agenciaEnvio: form.agenciaEnvio || undefined,
        planes,
      },
      ddjjConfig: ddjjConfigPayload(form, !!cuestionarioPropio),
      sinWizard: form.sinWizard,
    });
  };

  // Cuestionario propio GUARDADO (importado): reemplaza al catálogo del micrositio.
  const cuestionarioPropio = useMemo(() => {
    const cfg = empresaQ.data?.ddjjConfig;
    return cfg?.preguntas && cfg.preguntas.length > 0
      ? {
          preguntas: cfg.preguntas,
          origen: cfg.origen === 'excel' ? ('excel' as const) : ('binary' as const),
          actualizadoAt: cfg.preguntasActualizadasAt,
        }
      : null;
  }, [empresaQ.data]);

  // Hay plan opcional si existe un plan no-base con OTRO código: el mismo plan
  // en otra sucursal es la misma cobertura, no un upgrade (igual que el server).
  const planOpcional = useMemo(() => {
    const planes = form?.planes.filter((p) => p.codigo.trim()) ?? [];
    const baseCodigo = (planes.find((p) => p.esBase) ?? planes[0])?.codigo;
    return planes.some((p) => !p.esBase && p.codigo !== baseCodigo);
  }, [form]);

  if (empresaQ.isLoading || !form) {
    return (
      <section>
        <p className="text-element-mid">Cargando organización…</p>
      </section>
    );
  }
  if (empresaQ.isError) {
    return (
      <section>
        <p className="status status-error">No se pudo cargar la organización.</p>
        <button type="button" className="btn btn-ghost" onClick={() => navigate('/empresas')}>
          Volver
        </button>
      </section>
    );
  }

  return (
    <section>
      <header className="pageHeader">
        <button
          type="button"
          className="btn btn-link"
          style={{ padding: 0, marginBottom: 6 }}
          onClick={() => navigate('/empresas')}
        >
          <ArrowLeft size={15} /> Organizaciones
        </button>
        <h1 className="heading-3">{form.razonSocial || 'Organización'}</h1>
        <p className="text-element-mid">
          CUIT {form.cuit || '—'}
          {form.oracleEmplId && ` · empleador Oracle ${form.oracleEmplId}`}
          {' · '}
          {form.sinWizard
            ? 'sin asistente del titular (los padrones validados pasan directo al alta)'
            : planOpcional
              ? 'ofrece plan opcional (el titular elige en el wizard)'
              : 'sin plan opcional (el wizard omite el paso Plan)'}
        </p>
      </header>

      <nav
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--color-outline)',
          margin: '12px 0 16px',
          flexWrap: 'wrap',
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setParams({ tab: t.id }, { replace: true })}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '8px 14px',
              fontSize: 13.5,
              fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? 'var(--color-primary-darker)' : 'var(--color-element-mid)',
              borderBottom: `2px solid ${tab === t.id ? 'var(--color-primary)' : 'transparent'}`,
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {error && (
        <p className="status status-error" style={{ marginBottom: 12 }}>
          {error}
        </p>
      )}
      {okMsg && (
        <p className="status status-ok" style={{ marginBottom: 12 }}>
          {okMsg}
        </p>
      )}

      <div className="card" style={{ padding: 16 }}>
        {tab === 'datos' && (
          <DatosTab
            form={form}
            set={set}
            check={check.data}
            emplIdValido={emplIdValido}
            traerPending={traer.isPending}
            onTraer={() => traer.mutate()}
            notas={oracleNotas}
            resumen={oracleResumen}
          />
        )}

        {tab === 'planes' && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={form.altaHabilitada}
                onChange={(e) => set('altaHabilitada', e.target.checked)}
              />
              <span style={{ fontSize: 13.5 }}>Habilitar alta por API (Binary)</span>
            </label>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}
              title="Al validar un padrón, los legajos no reciben la invitación al onboarding (OTP, datos, DDJJ, plan): pasan directo a «Listo para alta» y el despacho a Binary los toma. Los bis del padrón quedan activados."
            >
              <input
                type="checkbox"
                checked={form.sinWizard}
                onChange={(e) => set('sinWizard', e.target.checked)}
              />
              <span style={{ fontSize: 13.5 }}>
                Sin asistente del titular: los legajos pasan directo al alta al validar el padrón
              </span>
            </label>
            {form.sinWizard && (
              <p className="text-element-mid" style={{ fontSize: 12.5, margin: '-4px 0 12px 24px' }}>
                No se manda ningún mail al titular y no se le pide la declaración jurada. Aplica a
                los padrones que se validen desde ahora; los ya validados no cambian.
              </p>
            )}
            <div className="modalGrid" style={{ marginBottom: 14 }}>
              <Campo
                label="Obra social (OSOC)"
                value={form.obraSocialCodigo}
                onChange={(v) => set('obraSocialCodigo', v)}
                placeholder="112"
              />
              <Campo
                label="Promotor"
                value={form.promotorId}
                onChange={(v) => set('promotorId', v)}
                placeholder="PROMDIR"
              />
              <Campo
                label="Firma solicitud"
                value={form.firmaSolicitud}
                onChange={(v) => set('firmaSolicitud', v)}
                placeholder="SALUD"
              />
              <Campo
                label="Agencia de envío"
                value={form.agenciaEnvio}
                onChange={(v) => set('agenciaEnvio', v)}
                placeholder="1"
              />
            </div>
            <PlanesLista
              planes={form.planes}
              onSetPlan={setPlan}
              onAddPlan={addPlan}
              onRemovePlan={removePlan}
              onSetBase={setBase}
              onToggleVenta={setVenta}
            />
          </>
        )}

        {tab === 'ddjj' && (
          <DdjjOrigenSelector
            excluidas={form.ddjjExcluidas}
            onExcluidas={(v) => set('ddjjExcluidas', v)}
            propio={form.ddjjPropio}
            onPropio={(v) => set('ddjjPropio', v)}
            actual={{
              origen: cuestionarioPropio?.origen ?? 'catalogo',
              preguntas: cuestionarioPropio?.preguntas.length ?? 0,
              actualizadoAt: cuestionarioPropio?.actualizadoAt,
            }}
            disabled={guardar.isPending}
          />
        )}

        {tab === 'usuarios' && <UsuariosDeEmpresa empresaId={id} />}
      </div>

      {tab !== 'usuarios' && puedeMutar && (
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={guardar.isPending}
          >
            {guardar.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/empresas')}>
            Volver
          </button>
        </div>
      )}
    </section>
  );
}

function DatosTab({
  form,
  set,
  check,
  emplIdValido,
  traerPending,
  onTraer,
  notas,
  resumen,
}: {
  form: FormState;
  set: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
  check?: { existe: boolean; planesVigentes: number; afiliadosActivos: number };
  emplIdValido: boolean;
  traerPending: boolean;
  onTraer: () => void;
  notas: string[];
  resumen: ResumenOracle;
}) {
  return (
    <>
      <div className="modalGrid">
        <Campo
          label="CUIT (11 dígitos sin guiones)"
          value={form.cuit}
          onChange={(v) => set('cuit', v)}
        />
        <Campo
          label="Razón social"
          value={form.razonSocial}
          onChange={(v) => set('razonSocial', v)}
        />
        <Campo
          label="Nombre comercial"
          value={form.nombreComercial}
          onChange={(v) => set('nombreComercial', v)}
        />
        <Campo
          label="Contacto — Nombre"
          value={form.contactoNombre}
          onChange={(v) => set('contactoNombre', v)}
        />
        <Campo
          label="Contacto — Email"
          value={form.contactoEmail}
          onChange={(v) => set('contactoEmail', v)}
        />
        <Campo
          label="Contacto — Teléfono"
          value={form.contactoTelefono}
          onChange={(v) => set('contactoTelefono', v)}
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <Campo
          label="EMPL_ID Oracle"
          value={form.oracleEmplId}
          onChange={(v) => set('oracleEmplId', v)}
          placeholder="74000"
        />
        {emplIdValido && check && (
          <p
            className="text-element-mid"
            style={{ fontSize: 12.5, marginTop: 6, color: check.existe ? '#166534' : '#b91c1c' }}
          >
            {check.existe
              ? `✓ Organización encontrada · ${check.planesVigentes} planes vigentes · ${check.afiliadosActivos.toLocaleString('es-AR')} afiliados activos.`
              : '✗ EMPL_ID no encontrado en Oracle.'}
          </p>
        )}
        <button
          type="button"
          className="btn btn-ghost"
          style={{ marginTop: 8 }}
          disabled={!emplIdValido || traerPending}
          onClick={onTraer}
        >
          <DownloadCloudIcon size={14} /> {traerPending ? 'Trayendo…' : 'Traer config de Oracle'}
        </button>
        {resumen.aplicados.length > 0 && (
          <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 12.5, color: '#166534' }}>
            {resumen.aplicados.map((n) => (
              <li key={n}>Se completó — {n}</li>
            ))}
          </ul>
        )}
        {resumen.procedencia.length > 0 && (
          <ul
            style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5 }}
            className="text-element-mid"
          >
            {resumen.procedencia.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}
        {resumen.distintos.length > 0 && (
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5, color: '#b45309' }}>
            {resumen.distintos.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}
        {notas.length > 0 && (
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5, color: '#b45309' }}>
            {notas.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function Campo({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label style={{ display: 'block', fontSize: 12, color: 'var(--color-element-mid)' }}>
      {label}
      <input
        className="input"
        style={{ marginTop: 3 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

